import {
  CenteredMessage,
  filterOutNullAndUndefined,
  getFirstGraphQLErrorCode,
  objectValues,
} from '@tableau/data-catalog-shared';
import { addStringListToKeySet, assertNever, MutableKeySet, stringListToKeySet } from '@tableau/data-structures';
import { VizPortalWebClientRequester } from '@tableau/vizportal-webclient-api';
import { ActivitySpinnerWidget } from '@tableau/widgets';
import { ApolloError, ApolloQueryResult } from 'apollo-client';
import * as React from 'react';
import { Query } from 'react-apollo';

import { Messages } from '../../../gen-src/Localize';
import { SheetFieldInstanceDatasourceFragment } from '../../../gen-src/queries/SheetFieldInstanceDatasourceFragment';
import {
  SheetFieldInstanceFragment_BinField_upstreamDatabasesConnection,
  SheetFieldInstanceFragment_CalculatedField_upstreamDatabasesConnection,
  SheetFieldInstanceFragment_ColumnField_upstreamDatabasesConnection,
  SheetFieldInstanceFragment_CombinedField_upstreamDatabasesConnection,
  SheetFieldInstanceFragment_CombinedSetField_upstreamDatabasesConnection,
  SheetFieldInstanceFragment_DatasourceField_upstreamDatabasesConnection,
  SheetFieldInstanceFragment_GroupField_upstreamDatabasesConnection,
  SheetFieldInstanceFragment_HierarchyField_upstreamDatabasesConnection,
  SheetFieldInstanceFragment_SetField_upstreamDatabasesConnection,
} from '../../../gen-src/queries/SheetFieldInstanceFragment';
import {
  ViewDetailsByPathQuery,
  ViewDetailsByPathQuery_views,
  ViewDetailsByPathQuery_views_Dashboard,
  ViewDetailsByPathQuery_views_Sheet,
  ViewDetailsByPathQuery_views_Sheet_datasourceFields,
  ViewDetailsByPathQuery_views_Sheet_worksheetFields,
  ViewDetailsByPathQueryVariables,
} from '../../../gen-src/queries/ViewDetailsByPathQuery';
import { ViewDetailsByPathQueryGql } from '../../queries/ViewDetailsByPathQueryGql';
import { LoadingFailedWidget } from './LoadingFailedWidget';
import { SheetField } from './SheetField';
import { lineagePanelBodyWithError, LineagePanelContainer, loadingIndicator } from './ViewerPaneStyles';
import { VizClientDatasource, VizClientPublishedDatasource } from './VizClientDatasourceElement';
import { VizClientLineagePanelBody } from './VizClientLineagePanelBody';
import { VizClientLineagePaneTitleBar } from './VizClientLineagePanelTitleBar';

export type ViewDetailsByPathWrapperProps = {
  readonly viewPath: string;
  readonly testId: string;
  readonly onCloseRequested: () => void;
  readonly lastRefreshKey?: string;
  readonly vizPortalWebClientRequester: VizPortalWebClientRequester;
};

export type SheetFieldCollection = {
  readonly worksheetFields: ReadonlyArray<SheetField>;
  readonly datasourceFields: ReadonlyArray<SheetField>;
};

const emptySheetFields: SheetFieldCollection = {
  worksheetFields: [],
  datasourceFields: []
};

type DatasourceMap = { [key: string]: VizClientDatasource };

type FieldIdContainer = ViewDetailsByPathQuery_views_Sheet_worksheetFields | ViewDetailsByPathQuery_views_Sheet_datasourceFields | null;

type Dashboard = ViewDetailsByPathQuery_views_Dashboard;
type Sheet = ViewDetailsByPathQuery_views_Sheet;
type View = ViewDetailsByPathQuery_views;

type RefetchType = (variables?: ViewDetailsByPathQueryVariables) => Promise<ApolloQueryResult<ViewDetailsByPathQuery>>;

type SheetFieldInstanceFragmentUpstreamDatabasesConnection =
  SheetFieldInstanceFragment_BinField_upstreamDatabasesConnection
  | SheetFieldInstanceFragment_CalculatedField_upstreamDatabasesConnection
  | SheetFieldInstanceFragment_ColumnField_upstreamDatabasesConnection
  | SheetFieldInstanceFragment_CombinedField_upstreamDatabasesConnection
  | SheetFieldInstanceFragment_CombinedSetField_upstreamDatabasesConnection
  | SheetFieldInstanceFragment_DatasourceField_upstreamDatabasesConnection
  | SheetFieldInstanceFragment_GroupField_upstreamDatabasesConnection
  | SheetFieldInstanceFragment_HierarchyField_upstreamDatabasesConnection
  | SheetFieldInstanceFragment_SetField_upstreamDatabasesConnection;

export function getSheetFieldInstances(view: View): SheetFieldCollection {
  switch (view.__typename) {
    case 'Dashboard': return getSheetFieldInstancesFromDashboard(view);
    case 'Sheet': return getSheetFieldInstancesFromSheet(view);
    default: assertNever(view); return emptySheetFields;
  }
}

function getFieldIdsFromCollection(collection: (FieldIdContainer[]) | null): string[] {
  if (!collection) { return []; }

  return filterOutNullAndUndefined(collection).map(f => f.id);
}

function getSheetFieldInstancesFromSheet(sheet: Sheet): SheetFieldCollection {
  if (!sheet.sheetFieldInstances) {
    return emptySheetFields;
  }

  const worksheetFields: SheetField[] = [];
  const datasourceFields: SheetField[] = [];

  const worksheetFieldIds = stringListToKeySet(getFieldIdsFromCollection(sheet.worksheetFields));
  const datasourceFieldIds = stringListToKeySet(getFieldIdsFromCollection(sheet.datasourceFields));

  sheet.sheetFieldInstances.forEach(field => {
    if (!field) { return; }

    if (datasourceFieldIds[field.id]) {
      datasourceFields.push(field);
    } else if (worksheetFieldIds[field.id]) {
      worksheetFields.push(field);
    }
  });

  return {
    worksheetFields,
    datasourceFields
  };
}

function getSheetFieldInstancesFromDashboard(dashboard: Dashboard): SheetFieldCollection {
  if (!dashboard.upstreamSheetFieldInstances) {
    return emptySheetFields;
  }

  const worksheetFields: SheetField[] = [];
  const datasourceFields: SheetField[] = [];

  const worksheetFieldIds: MutableKeySet = {};
  const datasourceFieldIds: MutableKeySet = {};

  const fieldIdsSeen: MutableKeySet = {};

  dashboard.sheets.forEach(sheet => {
    addStringListToKeySet(worksheetFieldIds, getFieldIdsFromCollection(sheet.worksheetFields));
    addStringListToKeySet(datasourceFieldIds, getFieldIdsFromCollection(sheet.datasourceFields));
  });

  dashboard.upstreamSheetFieldInstances.forEach(field => {
    if (!field || fieldIdsSeen[field.id]) {
      return;
    }

    fieldIdsSeen[field.id] = true;

    if (datasourceFieldIds[field.id]) {
      datasourceFields.push(field);
    } else if (worksheetFieldIds[field.id]) {
      worksheetFields.push(field);
    }
  });

  return {
    worksheetFields,
    datasourceFields
  };
}

export function getDatasourcesListFromDatasourceFields(fields: ReadonlyArray<SheetField>): VizClientDatasource[] {
  // Get datasources from fields list. Store them in an associative array by ID and
  // collect the values later.
  const datasourceMap: DatasourceMap = {};

  if (fields) {
    fields.forEach(field => {
      if (!field) {
        return;
      }

      // If this is a DatasourceField, it's connecting to a published datasource.
      // Make a note of that.
      let publishedDatasource: VizClientPublishedDatasource | null = null;

      // hasPublishedDatasource is necessary to disambiguate between if a datasource has no
      // published datasource vs if the published datasource is obfuscated
      let hasPublishedDatasource = false;

      if (field.__typename === 'DatasourceField') {
        hasPublishedDatasource = true;
        const remoteField = field.remoteField;
        if (remoteField && remoteField.datasource && remoteField.datasource.__typename === 'PublishedDatasource') {
          publishedDatasource = remoteField.datasource;
        }
      }

      // Fields without defined data sources do not factor into the list of datasources.
      // If we see the same embedded data source multiple times, but we do not yet have a
      // published data source, parse the field again to try finding a published data source.
      if (field.datasource && !datasourceMapsToPublishedDatasource(datasourceMap, field.datasource.id)) {
        datasourceMap[field.datasource.id] =
          datasourceToVizClientDatasource(
            field.datasource,
            publishedDatasource,
            hasPublishedDatasource,
            field.upstreamDatabasesConnection);
      }
    });
  }

  // True if the a DatasourceMap contains a given key and the key maps to a value with a
  // published data source.
  function datasourceMapsToPublishedDatasource(map: DatasourceMap, id: string): boolean {
    if (!map[id]) {
      return false; // map does not contain the given key
    }

    return map[id].hasPublishedDatasource;
  }

  // Hard-coding the language because sort behavior in Tableau isn't typically expected
  // to change based on the browser locale. This isn't ideal and should be revisited if
  // it is found to cause customer pain.
  return objectValues(datasourceMap).sort((a, b) =>
    (a.name || '').localeCompare((b.name || ''), 'en', { sensitivity: 'base' }));
}

// Convert a returnedDatasource into a datasource suitable for use in the component.
function datasourceToVizClientDatasource(
  datasource: SheetFieldInstanceDatasourceFragment,
  publishedDatasource: VizClientPublishedDatasource | null,
  hasPublishedDatasource: boolean,
  upstreamDatabasesConnection: SheetFieldInstanceFragmentUpstreamDatabasesConnection | null,
): VizClientDatasource {


  return {
    id: datasource.id,
    name: datasource.name,
    __typename: datasource.__typename,
    hasExtracts: datasource.hasExtracts,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    extractLastUpdateTime: datasource.extractLastUpdateTime,
    hasPublishedDatasource: hasPublishedDatasource,
    publishedDatasource: publishedDatasource,
    databasesConnectionNodes: upstreamDatabasesConnection ?
      upstreamDatabasesConnection.nodes : null
  };
}

export class ViewDetailsByPathWrapper extends React.Component<ViewDetailsByPathWrapperProps> {
  public render(): React.ReactNode {
    return (
      <Query<ViewDetailsByPathQuery, ViewDetailsByPathQueryVariables>
        query={ViewDetailsByPathQueryGql}
        variables={{ viewPath: this.props.viewPath }}
      >
        {({ loading, error, data, refetch }) => (
          <ViewDetailsByPathBody loading={loading} error={error} data={data} refetch={refetch} {...this.props} />
        )}
      </Query>
    );
  }
}

export type ViewDetailsByPathBodyProps = {
  readonly loading: boolean;
  readonly error: ApolloError | undefined;
  readonly data: ViewDetailsByPathQuery | undefined;
  readonly refetch: RefetchType;
} & ViewDetailsByPathWrapperProps;

export class ViewDetailsByPathBody extends React.Component<ViewDetailsByPathBodyProps> {
  public render(): React.ReactNode {
    if (this.props.loading) {
      return this.loadingIndicator();
    }
    // Ignore the PERMISSIONS_MODE_SWITCHED error, which occurs by design in a particular
    // catalog configuration.
    if (this.props.error && getFirstGraphQLErrorCode(this.props.error) !== 'PERMISSIONS_MODE_SWITCHED') {
      return this.renderError(this.props.error, this.props.refetch);
    }

    return this.renderViewDetails(this.props.data);
  }

  private readonly titleBar = (): React.ReactNode => {
    return (
      <VizClientLineagePaneTitleBar
        title={Messages.DataDetailsPaneTitle()}
        onCloseRequested={this.props.onCloseRequested}
      />
    );
  }

  private readonly renderViewDetails = (data: ViewDetailsByPathQuery | undefined): React.ReactNode => {
    // Querying a sheet by its own path should return exactly 1 sheet, unless the graph is not fully populated.
    // All sheets are expected to have a workbook.
    if (!data
      || !data.views
      || data.views.length === 0
      || !data.views[0]
      || !data.views[0].workbook) {
      return this.renderIngestionInProgress();
    }

    const responseView: ViewDetailsByPathQuery_views = data.views[0];
    const responseWorkbook = data.views[0].workbook;
    const responseOwner = responseWorkbook.owner;
    const { datasourceFields, worksheetFields } = getSheetFieldInstances(responseView);
    const datasources = getDatasourcesListFromDatasourceFields(datasourceFields);
    return (
      <LineagePanelContainer data-tb-test-id={this.props.testId}>
        {this.titleBar()}
        <VizClientLineagePanelBody
          viewName={responseView.name}
          viewType={responseView.__typename}
          workbookName={responseWorkbook.name}
          workbookVizportalUrlId={responseWorkbook.vizportalUrlId}
          projectName={responseWorkbook.projectName}
          projectVizportalUrlId={responseWorkbook.projectVizportalUrlId}
          owner={responseOwner}
          datasources={datasources}
          datasourceFields={datasourceFields}
          worksheetFields={worksheetFields}
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          modifiedDate={responseView.updatedAt}
          vizPortalWebClientRequester={this.props.vizPortalWebClientRequester}
          viewPath={this.props.viewPath}
        />
      </LineagePanelContainer>
    );
  }

  private readonly loadingIndicator = (): React.ReactNode => {
    return (
      <LineagePanelContainer data-tb-test-id={this.props.testId}>
        <div className={loadingIndicator} aria-busy='true'>
          <ActivitySpinnerWidget testId='lineage-loading-details' />
          <div className='lineage-loading-details-message'>{Messages.LoadingDataDetails()}</div>
        </div>
      </LineagePanelContainer>
    );
  }

  private readonly renderError = (error: ApolloError, refetch: RefetchType): React.ReactNode => {
    // If we received an empty list of errors, display a generic error.
    if (!error.graphQLErrors || error.graphQLErrors.length === 0) {
      return this.renderPaneWithError(Messages.DataDetailsGenericErrorDetails());
    }

    return (
      <LineagePanelContainer data-tb-test-id={this.props.testId}>
        {this.titleBar()}
        <LoadingFailedWidget
          key='loadingFailedWidget'
          errorCode={getFirstGraphQLErrorCode(error)}
          onRetryRequested={() => refetch({ viewPath: this.props.viewPath })}
        />
      </LineagePanelContainer>
    );
  }

  private readonly renderIngestionInProgress = () => {
    return this.renderPaneWithError(Messages.ErrorItemNotFound());
  }

  // Display a pane with a single error centered.
  private readonly renderPaneWithError = (errorMessage: string) => {
    return (
      <LineagePanelContainer data-tb-test-id={this.props.testId}>
        <VizClientLineagePaneTitleBar
          title={Messages.Error()}
          onCloseRequested={this.props.onCloseRequested}
        />
        <div className={lineagePanelBodyWithError}>
          <CenteredMessage message={errorMessage} />
        </div>
      </LineagePanelContainer>
    );
  }
}
